import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Check, Copy, TriangleAlert } from "lucide-react";
import { RoutePicker } from "~/components/route-picker";
import { Button } from "~/components/ui/button";
import { addressFor, resolveFields, toPlainAddress, type ResolvedAddressField, type RouteAddress } from "~/lib/addresses";
import { useMe } from "~/lib/auth";
import { useCopy } from "~/lib/clipboard";
import { useRouteSelection } from "~/lib/use-route-selection";

export const Route = createFileRoute("/_authed/_portal/addresses")({
  component: Addresses,
});

function Addresses() {
  const { data: me } = useMe();
  const selection = useRouteSelection();
  const { origin, mode, options } = selection;

  const ppCode = me?.ppCode ?? "";
  const warehouse = origin ? addressFor(origin, mode) : null;
  const route = ppCode ? warehouse : null;

  return (
    <div className="space-y-8">
      <header>
        <h1 className="type-title">Your shipping address</h1>
        <p className="text-muted-foreground type-body mt-2">
          Enter it at checkout when you shop abroad. We receive your parcel there and bring it to Kenya.
        </p>
      </header>

      <section className="space-y-3">
        <h2 id="shopping-from" className="type-headline">
          Where are you shopping?
        </h2>
        <RoutePicker route={selection} labelledBy="shopping-from" />
      </section>

      {origin && options.length > 0 && !warehouse && (
        <p className="text-muted-foreground type-body">We do not have a warehouse for this route yet. Pick another one above.</p>
      )}

      {warehouse && !ppCode && (
        <p role="alert" className="text-destructive type-body">
          We could not load your personal code, so the address would be incomplete. Reload the page to try again.
        </p>
      )}

      {route && <AddressSection key={`${origin}-${mode}`} route={route} ppCode={ppCode} />}

      {route && (
        <section className="border-hairline border-t pt-6">
          <h2 className="type-headline">After you order</h2>
          <p className="text-muted-foreground type-body mt-1">
            The seller gives you a tracking number. Add it here so we know your parcel is coming.
          </p>
          <Button asChild variant="outline" size="touch" className="mt-4 w-full">
            <Link to="/register" search={{ origin, mode, step: 2 }}>
              Add my tracking number
              <ArrowRight />
            </Link>
          </Button>
        </section>
      )}
    </div>
  );
}

function AddressSection({ route, ppCode }: { route: RouteAddress; ppCode: string }) {
  const fields = resolveFields(route.fields, ppCode);
  const { copied, copy } = useCopy();

  return (
    <section className="space-y-4">
      <div>
        <h2 className="type-headline">Enter this at checkout</h2>
        <p className="text-muted-foreground type-footnote mt-1">Tap a line to copy it into the store's form.</p>
      </div>

      <div className="border-hairline bg-card divide-hairline divide-y overflow-hidden rounded-lg border">
        {fields.map((field) => (
          <AddressLine key={field.label} field={field} />
        ))}
      </div>

      <p className="text-muted-foreground type-footnote">
        Keep every line exactly as shown. A parcel that arrives without your code cannot be matched to you.
      </p>

      {route.packing && <PackingNotice text={route.packing} mandatory={!!route.packingIsMandatory} />}

      <Button size="touch" className="w-full" onClick={() => copy(toPlainAddress(fields))}>
        {copied ? <Check /> : <Copy />}
        {copied ? "Address copied" : "Copy full address"}
      </Button>
    </section>
  );
}

function AddressLine({ field }: { field: ResolvedAddressField }) {
  const { copied, copy } = useCopy(1200);
  return (
    <button
      type="button"
      onClick={() => copy(field.value)}
      className="hover:bg-accent/50 flex w-full items-center gap-3 px-4 py-3 text-left transition-colors"
    >
      <span className="min-w-0 flex-1">
        <span className="text-muted-foreground flex items-center gap-2 text-[13px]">
          {field.label}
          {field.carriesCode && (
            <span className="bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-[11px] leading-none font-medium">
              Your code
            </span>
          )}
        </span>
        <span className="mt-0.5 block text-[16px] font-medium break-words">{field.value}</span>
        {field.note && <span className="text-muted-foreground mt-1 block text-[13px] leading-snug">{field.note}</span>}
      </span>
      {copied ? (
        <span role="status" className="flex shrink-0 items-center gap-1 text-[13px] font-medium">
          <Check className="size-4" />
          Copied
        </span>
      ) : (
        <Copy className="text-muted-foreground size-4.5 shrink-0" aria-label="Copy" />
      )}
    </button>
  );
}

function PackingNotice({ text, mandatory }: { text: string; mandatory: boolean }) {
  if (!mandatory) {
    return (
      <p className="type-footnote">
        <span className="font-medium">Good to know. </span>
        <span className="text-muted-foreground">{text}</span>
      </p>
    );
  }
  return (
    <div className="border-warning/50 bg-warning/10 flex gap-3 rounded-lg border p-4">
      <TriangleAlert className="mt-0.5 size-5 shrink-0" strokeWidth={1.75} aria-hidden />
      <div>
        <p className="text-[15px] font-semibold">Required by this warehouse</p>
        <p className="type-footnote mt-1">{text}</p>
      </div>
    </div>
  );
}
